<script setup lang="ts">
import type { GlobeInstance } from 'globe.gl';
import { ref, onMounted, onUnmounted } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { getPois } from './utils/geojsonToPoisArray';
import type { Poi } from './types';
import poiIcon from './assets/begreppa.svg?raw';
import earthHighResUrl from './assets/blue_marble_8192x4096.jpg';
import { usePlanetEarthTracking } from './composables/usePlanetEarthTracking';

const props = defineProps<{
  geoJson: string;
}>();

const emits = defineEmits<{
  'poi-clicked': [pois: Poi[]];
  'poi-closed': [];
}>();

const globeDiv = ref<HTMLDivElement | null>(null);
let myGlobe: GlobeInstance | null = null;

const { sendPoiClickedEvent } = usePlanetEarthTracking();

const openInfoDialog = (pois: Poi[]) => {
  sendPoiClickedEvent(pois[0].name);
  emits('poi-clicked', pois);
};

// Zoom limits in globe.gl altitude (globe radii above the surface); 2.5 is the altitude globe.gl starts at.
const MIN_ALTITUDE = 0.1;
const MAX_ALTITUDE = 2.5;
const MIN_POI_SCALE = 1;
const MAX_POI_SCALE = 2.2;

const updatePoiScale = (altitude: number) => {
  if (!globeDiv.value) {
    return;
  }
  const t = Math.min(Math.max((altitude - MIN_ALTITUDE) / (MAX_ALTITUDE - MIN_ALTITUDE), 0), 1);
  const scale = MAX_POI_SCALE - t * (MAX_POI_SCALE - MIN_POI_SCALE);
  globeDiv.value.style.setProperty('--poi-scale', scale.toString());
};

// POIs closer than this share one marker. MIN_ALTITUDE lets POIs further apart, like Southampton and Stonehenge
// (42 km), separate when fully zoomed in.
const BUNDLE_DISTANCE_KM = 20;
const EARTH_RADIUS_KM = 6371;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const distanceKm = (a: Poi, b: Poi) => {
  const haversine =
    Math.sin(toRadians(b.lat - a.lat) / 2) ** 2 +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(toRadians(b.lng - a.lng) / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(haversine));
};

const bundleNearbyPois = (pois: Poi[]) =>
  pois.reduce<Poi[][]>((bundles, poi) => {
    const isNearby = (bundle: Poi[]) => bundle.some((p) => distanceKm(p, poi) < BUNDLE_DISTANCE_KM);
    return [...bundles.filter((bundle) => !isNearby(bundle)), [...bundles.filter(isNearby).flat(), poi]];
  }, []);

const ICON_ID_PATTERN = /(poiClip|poiGrad[0-5])/g;
let poiIconInstanceCount = 0;
const createUniquePoiIcon = () => {
  const suffix = `-${poiIconInstanceCount++}`;
  return poiIcon.replace(ICON_ID_PATTERN, (id) => `${id}${suffix}`);
};

// Forwarding pointerdown to the canvas (so drags starting on a POI rotate the globe) makes the canvas
// capture the pointer, so the POI element never receives its own pointerup/click. Detect taps manually.
const CLICK_MOVE_THRESHOLD = 6;
const pendingPoiClicks = new Map<number, { pois: Poi[]; x: number; y: number }>();

const handlePointerUp = (e: PointerEvent) => {
  const pending = pendingPoiClicks.get(e.pointerId);
  if (!pending) {
    return;
  }
  pendingPoiClicks.delete(e.pointerId);
  const distance = Math.hypot(e.clientX - pending.x, e.clientY - pending.y);
  if (distance < CLICK_MOVE_THRESHOLD) {
    openInfoDialog(pending.pois);
  }
};

const handlePointerCancel = (e: PointerEvent) => {
  pendingPoiClicks.delete(e.pointerId);
};

onMounted(() => {
  document.addEventListener('pointerup', handlePointerUp);
  document.addEventListener('pointercancel', handlePointerCancel);
});

onUnmounted(() => {
  document.removeEventListener('pointerup', handlePointerUp);
  document.removeEventListener('pointercancel', handlePointerCancel);
});

onMounted(async () => {
  // globe.gl and three.js are about 2 MB, so they are only downloaded when the game is opened.
  const { default: Globe } = await import('globe.gl');
  if (globeDiv.value) {
    const poiBundles = bundleNearbyPois(getPois(props.geoJson));
    myGlobe = new Globe(globeDiv.value)
      .width(globeDiv.value.clientWidth)
      .height(globeDiv.value.clientHeight)
      .globeImageUrl(earthHighResUrl)
      .htmlElementsData(poiBundles)
      .htmlLat((d: object) => (d as Poi[])[0].lat)
      .htmlLng((d: object) => (d as Poi[])[0].lng)
      .htmlElement((d: object) => {
        const pois = d as Poi[];
        const el = document.createElement('div');
        el.classList.add('poi-marker');
        el.style.pointerEvents = 'auto';
        el.addEventListener(
          'wheel',
          (e: WheelEvent) => {
            e.preventDefault();
            globeDiv.value?.querySelector('canvas')?.dispatchEvent(new WheelEvent('wheel', e));
          },
          { passive: false },
        );
        el.addEventListener(
          'pointerdown',
          (e: PointerEvent) => {
            e.preventDefault();
            pendingPoiClicks.set(e.pointerId, { pois, x: e.clientX, y: e.clientY });
            globeDiv.value?.querySelector('canvas')?.dispatchEvent(new PointerEvent('pointerdown', e));
          },
          { passive: false },
        );

        const inner = document.createElement('div');
        inner.classList.add('poi-marker-inner');
        inner.innerHTML = createUniquePoiIcon();
        el.appendChild(inner);

        return el;
      })
      .onZoom(({ altitude }) => updatePoiScale(altitude));
    const controls = myGlobe.controls();
    controls.minDistance = myGlobe.getGlobeRadius() * (1 + MIN_ALTITUDE);
    controls.maxDistance = myGlobe.getGlobeRadius() * (1 + MAX_ALTITUDE);
  }
});

useResizeObserver(globeDiv, (entries) => {
  if (!myGlobe) {
    return;
  }
  const { width, height } = entries[0].contentRect;
  myGlobe.width(width).height(height);
});
</script>
<template>
  <div ref="globeDiv" class="w-100 h-100"></div>
</template>

<style scoped lang="scss">
:deep(.poi-marker) {
  width: 18px;
  height: 18px;
  cursor: pointer;
  pointer-events: auto;
  touch-action: none;
}

:deep(.poi-marker-inner) {
  width: 100%;
  height: 100%;
  transition: transform 150ms ease;
  transform: scale(var(--poi-scale, 1));

  svg {
    width: 100%;
    height: 100%;
  }
}

:deep(.poi-marker:hover .poi-marker-inner) {
  transform: scale(calc(var(--poi-scale, 1) * 1.4));
}
</style>
